import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, ProtectedRoute, AdminRoute } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Analytics from './components/Analytics';
import Layout from './components/Layout';
import PublicLayout from './components/PublicLayout';
import AdminLayout from './components/AdminLayout';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import LegacySetupRedirect from './components/LegacySetupRedirect';

const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Domains = lazy(() => import('./pages/Domains'));
const Certificates = lazy(() => import('./pages/Certificates'));
const RenewalAlerts = lazy(() => import('./pages/RenewalAlerts'));
const SslMonitoring = lazy(() => import('./pages/SslMonitoring'));
const Agency = lazy(() => import('./pages/Agency'));
const Policies = lazy(() => import('./pages/Policies'));
const CertificateDiscovery = lazy(() => import('./pages/CertificateDiscovery'));
const NewDomainWizard = lazy(() => import('./pages/NewDomainWizard'));
const Agents = lazy(() => import('./pages/Agents'));
const Gateways = lazy(() => import('./pages/Gateways'));
const Profile = lazy(() => import('./pages/Profile'));
const Wallet = lazy(() => import('./pages/Wallet'));
const BuySsl = lazy(() => import('./pages/BuySsl'));
const SslOrders = lazy(() => import('./pages/SslOrders'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const FreeTools = lazy(() => import('./pages/FreeTools'));
const Features = lazy(() => import('./pages/Features'));
const CentralizedManagement = lazy(() => import('./pages/products/CentralizedManagement'));
const WindowsAgent = lazy(() => import('./pages/products/WindowsAgent'));
const IISIntegration = lazy(() => import('./pages/integrations/IISIntegration'));
const LinuxIntegration = lazy(() => import('./pages/integrations/LinuxIntegration'));
const ApacheIntegration = lazy(() => import('./pages/integrations/ApacheIntegration'));
const TomcatIntegration = lazy(() => import('./pages/integrations/TomcatIntegration'));
const IbmHttpIntegration = lazy(() => import('./pages/integrations/IbmHttpIntegration'));
const F5BigIpIntegration = lazy(() => import('./pages/integrations/F5BigIpIntegration'));
const CitrixIntegration = lazy(() => import('./pages/integrations/CitrixIntegration'));
const A10Integration = lazy(() => import('./pages/integrations/A10Integration'));
const AWSIntegration = lazy(() => import('./pages/integrations/AWSIntegration'));
const GoogleCloudIntegration = lazy(() => import('./pages/integrations/GoogleCloudIntegration'));
const AzureIntegration = lazy(() => import('./pages/integrations/AzureIntegration'));
const CpanelIntegration = lazy(() => import('./pages/integrations/CpanelIntegration'));
const PleskIntegration = lazy(() => import('./pages/integrations/PleskIntegration'));
const Pricing = lazy(() => import('./pages/Pricing'));
const Docs = lazy(() => import('./pages/Docs'));
const Enterprise = lazy(() => import('./pages/Enterprise'));
const EnterpriseSsl = lazy(() => import('./pages/EnterpriseSsl'));
const Msps = lazy(() => import('./pages/Msps'));
const Security = lazy(() => import('./pages/Security'));
const Community = lazy(() => import('./pages/Community'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const About = lazy(() => import('./pages/About'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Refund = lazy(() => import('./pages/Refund'));
const Status = lazy(() => import('./pages/Status'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTenants = lazy(() => import('./pages/admin/AdminTenants'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminDomains = lazy(() => import('./pages/admin/AdminDomains'));
const AdminCertificates = lazy(() => import('./pages/admin/AdminCertificates'));
const AdminAgents = lazy(() => import('./pages/admin/AdminAgents'));
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminPricing = lazy(() => import('./pages/admin/AdminPricing'));

function RouteFallback() {
  return (
    <div className="min-h-[30vh] flex items-center justify-center bg-dark-900 text-gray-500 text-sm">
      Yükleniyor…
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Analytics />
      <AuthProvider>
        <WorkspaceProvider>
        <NotificationProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Turkish auth routes: PublicLayout links to these via lPath(), so without
                  them the catch-all bounces Turkish visitors back to the landing page. */}
              <Route path="/tr/login" element={<Login />} />
              <Route path="/tr/register" element={<Register />} />
              <Route path="/tr/verify-email" element={<VerifyEmail />} />
              <Route path="/tr/forgot-password" element={<ForgotPassword />} />
              <Route path="/tr/reset-password" element={<ResetPassword />} />

              <Route path="/" element={<PublicLayout />}>
                <Route path="tools" element={<FreeTools />} />
                <Route path="tools/:toolId" element={<FreeTools />} />
                <Route index element={<LandingPage />} />
                <Route path="features" element={<Features />} />
                <Route path="products/centralized-management" element={<CentralizedManagement />} />
                <Route path="products/windows-agent" element={<WindowsAgent />} />
                <Route path="integrations/iis" element={<IISIntegration />} />
                <Route path="integrations/linux" element={<LinuxIntegration />} />
                <Route path="integrations/apache" element={<ApacheIntegration />} />
                <Route path="integrations/tomcat" element={<TomcatIntegration />} />
                <Route path="integrations/ibm-http" element={<IbmHttpIntegration />} />
                <Route path="integrations/f5-big-ip" element={<F5BigIpIntegration />} />
                <Route path="integrations/citrix" element={<CitrixIntegration />} />
                <Route path="integrations/a10" element={<A10Integration />} />
                <Route path="integrations/aws" element={<AWSIntegration />} />
                <Route path="integrations/google-cloud" element={<GoogleCloudIntegration />} />
                <Route path="integrations/azure" element={<AzureIntegration />} />
                <Route path="integrations/cpanel" element={<CpanelIntegration />} />
                <Route path="integrations/plesk" element={<PleskIntegration />} />
                <Route path="pricing" element={<Pricing />} />
                <Route path="docs" element={<Docs />} />
                <Route path="enterprise" element={<Enterprise />} />
                <Route path="enterprise-ssl" element={<EnterpriseSsl />} />
                <Route path="msps" element={<Msps />} />
                <Route path="security" element={<Security />} />
                <Route path="community" element={<Community />} />
                <Route path="blog" element={<Blog />} />
                <Route path="blog/:slug" element={<BlogPost />} />
                <Route path="about" element={<About />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="terms" element={<Terms />} />
                <Route path="refund" element={<Refund />} />
                <Route path="status" element={<Status />} />
              </Route>

              <Route path="/tr" element={<PublicLayout />}>
                <Route path="tools" element={<FreeTools />} />
                <Route path="tools/:toolId" element={<FreeTools />} />
                <Route index element={<LandingPage />} />
                <Route path="features" element={<Features />} />
                <Route path="products/centralized-management" element={<CentralizedManagement />} />
                <Route path="products/windows-agent" element={<WindowsAgent />} />
                <Route path="integrations/iis" element={<IISIntegration />} />
                <Route path="integrations/linux" element={<LinuxIntegration />} />
                <Route path="integrations/apache" element={<ApacheIntegration />} />
                <Route path="integrations/tomcat" element={<TomcatIntegration />} />
                <Route path="integrations/ibm-http" element={<IbmHttpIntegration />} />
                <Route path="integrations/f5-big-ip" element={<F5BigIpIntegration />} />
                <Route path="integrations/citrix" element={<CitrixIntegration />} />
                <Route path="integrations/a10" element={<A10Integration />} />
                <Route path="integrations/aws" element={<AWSIntegration />} />
                <Route path="integrations/google-cloud" element={<GoogleCloudIntegration />} />
                <Route path="integrations/azure" element={<AzureIntegration />} />
                <Route path="integrations/cpanel" element={<CpanelIntegration />} />
                <Route path="integrations/plesk" element={<PleskIntegration />} />
                <Route path="pricing" element={<Pricing />} />
                <Route path="docs" element={<Docs />} />
                <Route path="enterprise" element={<Enterprise />} />
                <Route path="kurumsal-ssl-yonetimi" element={<EnterpriseSsl />} />
                <Route path="msps" element={<Msps />} />
                <Route path="security" element={<Security />} />
                <Route path="community" element={<Community />} />
                <Route path="blog" element={<Blog />} />
                <Route path="blog/:slug" element={<BlogPost />} />
                <Route path="about" element={<About />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="terms" element={<Terms />} />
                <Route path="refund" element={<Refund />} />
                <Route path="status" element={<Status />} />
              </Route>

              <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<Dashboard />} />
                <Route path="domains" element={<Domains />} />
                <Route path="certificates" element={<Certificates />} />
                <Route path="alerts" element={<RenewalAlerts />} />
                <Route path="ssl-monitor" element={<SslMonitoring />} />
                <Route path="agency" element={<Agency />} />
                <Route path="policies" element={<Policies />} />
                <Route path="discovery" element={<CertificateDiscovery />} />
                <Route path="setup" element={<LegacySetupRedirect />} />
                <Route path="domains/new" element={<LegacySetupRedirect />} />
                <Route path="agents" element={<Agents />} />
                <Route path="gateways" element={<Gateways />} />
                <Route path="agents/install" element={<NewDomainWizard key="agent-install" method="agent" />} />
                <Route path="gateways/install" element={<NewDomainWizard key="gateway-install" method="gateway" />} />
                <Route path="ssl/buy" element={<BuySsl />} />
                <Route path="ssl/orders" element={<SslOrders />} />
                <Route path="wallet" element={<Wallet />} />
                <Route path="profile" element={<Profile />} />
              </Route>

              <Route path="/controlpanel" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="tenants" element={<AdminTenants />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="domains" element={<AdminDomains />} />
                <Route path="certificates" element={<AdminCertificates />} />
                <Route path="agents" element={<AdminAgents />} />
                <Route path="jobs" element={<AdminJobs />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="pricing" element={<AdminPricing />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </NotificationProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
